/**
 * Reservations Page - Scaffolded for Internal Preview
 * Displays reservation diary with placeholder data
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Users, Plus, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { reservationService } from '@/services/api';

export default function ReservationsPage() {
  const { currentOutlet, outlets, loading: authLoading } = useAuth();
  // Use first outlet if none selected
  const outletId = currentOutlet?.id || outlets?.[0]?.id;
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDiary = async () => {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }
      
      if (!outletId) {
        setLoading(false);
        setError('No outlet selected');
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        const data = await reservationService.getDiary(outletId, date);
        setDiary(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load diary:', err);
        // Provide more specific error messages
        if (err.response?.status === 401) {
          setError('Session expired. Please refresh the page or log in again.');
        } else if (err.response?.status === 404) {
          setError('Outlet not found');
        } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          setError('Request timed out. Please try again.');
        } else {
          setError('Failed to load reservations. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDiary();
  }, [outletId, date, authLoading]);

  const servicePeriods = diary?.service_periods || [];
  const tables = diary?.tables || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-gray-900">Reservations</h1>
          <p className="text-gray-500 text-sm mt-1">
            {currentOutlet?.name || 'Select outlet'} - Table diary and bookings
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setDate(new Date().toISOString().split('T')[0])}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Today
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            New Booking
          </Button>
        </div>
      </div>

      {/* Date Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-gray-500" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border rounded-md px-3 py-2"
            />
            <Badge variant="outline">{diary?.total_reservations || 0} reservations</Badge>
            <Badge variant="outline">{diary?.total_covers || 0} covers</Badge>
          </div>
        </CardContent>
      </Card>

      {/* External Integration Notice */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex items-center gap-3">
          <ExternalLink className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">ResDiary Integration</p>
            <p className="text-xs text-amber-600">External reservation system integration is planned for Phase 2</p>
          </div>
          <Badge variant="outline" className="text-amber-600 border-amber-300">Stub Only</Badge>
        </CardContent>
      </Card>

      {/* Diary Grid */}
      {loading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading diary...</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setError(null);
                setLoading(true);
                reservationService.getDiary(outletId, date)
                  .then(data => {
                    setDiary(data);
                    setError(null);
                  })
                  .catch(err => {
                    console.error('Retry failed:', err);
                    if (err.response?.status === 401) {
                      setError('Session expired. Please refresh the page or log in again.');
                    } else {
                      setError('Failed to load reservations. Please try again.');
                    }
                  })
                  .finally(() => setLoading(false));
              }}
              data-testid="retry-diary-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Service Periods
            </CardTitle>
          </CardHeader>
          <CardContent>
            {servicePeriods.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No service periods configured</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {servicePeriods.map((period) => (
                  <Card key={period.id} className="border-gray-200">
                    <CardContent className="p-4">
                      <h3 className="font-semibold">{period.name}</h3>
                      <p className="text-sm text-gray-500">{period.start_time} - {period.end_time}</p>
                      <div className="mt-2 flex gap-2">
                        <Badge variant="secondary">{period.reservation_count || 0} bookings</Badge>
                        <Badge variant="outline">{period.covers || 0} covers</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tables Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Tables ({tables.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tables.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No tables configured for this outlet</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {tables.map((table) => (
                <div
                  key={table.id}
                  className={`p-3 rounded-lg border-2 text-center cursor-pointer transition-all ${
                    table.current_status === 'occupied'
                      ? 'border-red-300 bg-red-50'
                      : table.current_status === 'reserved'
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-green-300 bg-green-50'
                  }`}
                >
                  <p className="font-bold text-lg">{table.table_number}</p>
                  <p className="text-xs text-gray-500">{table.min_capacity}-{table.max_capacity} guests</p>
                  <Badge
                    variant={table.current_status === 'available' ? 'default' : 'secondary'}
                    className="mt-1 text-xs"
                  >
                    {table.current_status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
